import React, { useEffect } from 'react';
import './ConfirmModal.css';

/**
 * Reusable Yes/No confirmation modal.
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {function} onClose - Called when user clicks No or overlay or close icon
 * @param {function} onConfirm - Called when user clicks Yes
 * @param {string} title - Modal title
 * @param {string} message - Body message
 * @param {string} confirmLabel - Yes button label (e.g. "Yes, Delete")
 * @param {string} cancelLabel - No button label (e.g. "Cancel")
 * @param {boolean} isLoading - Disable buttons and show loading on confirm button
 * @param {string} loadingLabel - Text shown on confirm button when isLoading (e.g. "Deleting...")
 * @param {string} confirmVariant - 'danger' (red) or 'primary' (default)
 */
const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Yes',
  cancelLabel = 'No',
  isLoading = false,
  loadingLabel,
  confirmVariant = 'danger',
}) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) onClose?.();
  };

  if (!isOpen) return null;

  return (
    <div
      className="confirm-modal-overlay"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
    >
      <div className="confirm-modal-content">
        <div className="confirm-modal-header">
          <h3 id="confirm-modal-title" className="confirm-modal-title">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="confirm-modal-close"
            aria-label={cancelLabel}
            disabled={isLoading}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        <div className="confirm-modal-body">
          <p className="confirm-modal-message">{message}</p>
        </div>
        <div className="confirm-modal-footer">
          <button
            type="button"
            onClick={onClose}
            className="confirm-modal-btn confirm-modal-btn-cancel"
            disabled={isLoading}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`confirm-modal-btn confirm-modal-btn-confirm confirm-modal-btn-${confirmVariant}`}
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="confirm-modal-loading">
                <span className="confirm-modal-spinner" />
                {loadingLabel ?? confirmLabel}
              </span>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
